import { useState } from 'react';
import { ExclamationTriangleIcon, TrashIcon, XMarkIcon, ShieldExclamationIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../../../lib/supabase';
import { useCurrentUser } from '../../../../hooks/useCurrentUser';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

type Step = 'info' | 'confirm' | 'typing';

export default function MobileSettingsAccount() {
  const { user } = useCurrentUser();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('info');
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const CONFIRM_PHRASE = 'DELETE MY ACCOUNT';
  const isConfirmValid = confirmText === CONFIRM_PHRASE;

  const handleDelete = async () => {
    if (!isConfirmValid || !user) return;
    setDeleting(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) throw new Error('Not authenticated');

      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-account`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? 'Failed to delete account');
      }

      await supabase.auth.signOut();
      toast.success('Account closed successfully');
      navigate('/m');
    } catch (err: any) {
      toast.error(err.message ?? 'Failed to close account');
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Account info card */}
      <div className="bg-white rounded-2xl border border-gray-100 p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
            <ShieldExclamationIcon className="w-5 h-5 text-gray-500" />
          </div>
          <div>
            <p className="text-sm font-semibold text-golf-gray">Your Account</p>
            <p className="text-xs text-gray-400 capitalize">{user?.role ?? 'Admin'} account</p>
          </div>
        </div>
        <p className="text-xs text-gray-500 leading-relaxed">
          Closing your account will permanently remove your admin access and all associated data. This action cannot be undone.
        </p>
      </div>

      {/* What gets deleted */}
      <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4">
        <p className="text-xs font-semibold text-amber-800 mb-2 flex items-center gap-1.5">
          <ExclamationTriangleIcon className="w-4 h-4" />
          What will be removed
        </p>
        <ul className="space-y-1">
          {[
            'Your login credentials and session',
            'Your admin role and permissions',
            'Your user profile and account record',
          ].map((item) => (
            <li key={item} className="text-xs text-amber-700 flex items-start gap-1.5">
              <span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
              {item}
            </li>
          ))}
        </ul>
      </div>

      {/* Step: info */}
      {step === 'info' && (
        <button
          onClick={() => setStep('confirm')}
          className="w-full flex items-center justify-center gap-2 bg-red-50 border border-red-100 text-red-600 font-semibold text-sm py-3.5 rounded-2xl active:opacity-80 transition-opacity"
        >
          <TrashIcon className="w-4 h-4" />
          Close My Account
        </button>
      )}

      {/* Step: confirm */}
      {step === 'confirm' && (
        <div className="bg-white rounded-2xl border border-red-100 p-4 space-y-4">
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm font-semibold text-red-600">Are you absolutely sure?</p>
            <button onClick={() => setStep('info')} className="text-gray-400 active:opacity-60">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          <p className="text-xs text-gray-500 leading-relaxed">
            This will permanently close your account. You will lose all admin access immediately and will not be able to recover your account.
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setStep('info')}
              className="flex-1 py-3 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 active:opacity-80"
            >
              Cancel
            </button>
            <button
              onClick={() => setStep('typing')}
              className="flex-1 py-3 rounded-xl bg-red-500 text-white text-sm font-semibold active:opacity-80"
            >
              Yes, continue
            </button>
          </div>
        </div>
      )}

      {/* Step: typing confirmation */}
      {step === 'typing' && (
        <div className="bg-white rounded-2xl border border-red-100 p-4 space-y-4">
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm font-semibold text-red-600">Final confirmation</p>
            <button onClick={() => { setStep('info'); setConfirmText(''); }} className="text-gray-400 active:opacity-60">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-2">
              Type <span className="font-mono font-bold text-gray-800">{CONFIRM_PHRASE}</span> to confirm.
            </p>
            <input
              type="text"
              value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              placeholder={CONFIRM_PHRASE}
              autoCapitalize="characters"
              className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-red-400 transition-colors"
            />
          </div>
          <button
            onClick={handleDelete}
            disabled={!isConfirmValid || deleting}
            className="w-full py-3 rounded-xl bg-red-500 text-white text-sm font-semibold disabled:opacity-40 active:opacity-80 transition-opacity flex items-center justify-center gap-2"
          >
            {deleting ? (
              <>
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Closing account...
              </>
            ) : (
              <>
                <TrashIcon className="w-4 h-4" />
                Permanently Close Account
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
